import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  LocalAtm as LocalAtmIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Transaction {
  id: string;
  date: Date;
  invoiceNumber: string;
  supplierName: string;
  description: string;
  amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  paymentMethod: string;
  parts: {
    name: string;
    quantity: number;
    unitPrice: number;
  }[];
}

interface AdvancedFilters {
  dateRange: {
    start: string;
    end: string;
  };
  suppliers: string[];
  paymentMethods: string[];
  amountRange: {
    min: number;
    max: number;
  };
}

export const TransactionManagement: React.FC = () => {
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('this-month');
  const [openAdvancedFiltersDialog, setOpenAdvancedFiltersDialog] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    dateRange: {
      start: '',
      end: '',
    },
    suppliers: [],
    paymentMethods: [],
    amountRange: {
      min: 0,
      max: 1000000,
    },
  });

  // Mock transaction data
  const transactions: Transaction[] = [
    {
      id: 'TRX001',
      date: new Date(2024, 2, 15),
      invoiceNumber: 'INV-2024-001',
      supplierName: 'Ferrari South Africa',
      description: 'Carbon Ceramic Brake System - 488 Pista',
      amount: 212750,
      status: 'paid',
      paymentMethod: 'Bank Transfer',
      parts: [
        {
          name: 'Carbon Ceramic Brake System',
          quantity: 1,
          unitPrice: 212750,
        }
      ]
    },
    {
      id: 'TRX002',
      date: new Date(2024, 2, 14),
      invoiceNumber: 'INV-2024-002',
      supplierName: 'McLaren South Africa',
      description: 'Engine Control Unit - 765LT',
      amount: 245000,
      status: 'pending',
      paymentMethod: 'Credit Card',
      parts: [
        {
          name: 'Engine Control Unit',
          quantity: 1,
          unitPrice: 245000,
        }
      ]
    },
    {
      id: 'TRX003',
      date: new Date(2024, 2, 13),
      invoiceNumber: 'INV-2024-003',
      supplierName: 'Porsche Centre SA',
      description: 'Aerodynamic Package - 911 GT3 RS',
      amount: 385000,
      status: 'paid',
      paymentMethod: 'Bank Transfer',
      parts: [
        {
          name: 'Front Splitter',
          quantity: 1,
          unitPrice: 185000,
        },
        {
          name: 'Rear Wing',
          quantity: 1,
          unitPrice: 200000,
        }
      ]
    },
    {
      id: 'TRX004',
      date: new Date(2024, 2, 12),
      invoiceNumber: 'INV-2024-004',
      supplierName: 'Lamborghini Johannesburg',
      description: 'Titanium Exhaust System - Aventador SVJ',
      amount: 875000,
      status: 'overdue',
      paymentMethod: 'Bank Transfer',
      parts: [
        {
          name: 'Titanium Exhaust System',
          quantity: 1,
          unitPrice: 875000,
        }
      ]
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const calculateTotalAmount = (status?: string) => {
    return transactions
      .filter(t => !status || t.status === status)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setOpenDialog(true);
  };

  const handleAdvancedFiltersClick = () => {
    setOpenAdvancedFiltersDialog(true);
  };

  const handleApplyAdvancedFilters = () => {
    // Here you would typically filter the transactions based on the selected filters
    setOpenAdvancedFiltersDialog(false);
  };

  const handleExportClick = () => {
    // Create CSV content
    const headers = ['Date', 'Invoice #', 'Supplier', 'Description', 'Amount', 'Status', 'Payment Method'];
    const csvContent = [
      headers.join(','),
      ...transactions.map(transaction => [
        formatDate(transaction.date),
        transaction.invoiceNumber,
        transaction.supplierName,
        transaction.description,
        transaction.amount,
        transaction.status,
        transaction.paymentMethod
      ].join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Transaction Management
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Transactions
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="success.main" noWrap>
                  Paid
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('paid'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactions.filter(t => t.status === 'paid').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main" noWrap>
                  Pending
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('pending'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactions.filter(t => t.status === 'pending').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="error.main" noWrap>
                  Overdue
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('overdue'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactions.filter(t => t.status === 'overdue').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Transactions</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="this-week">This Week</MenuItem>
                  <MenuItem value="this-month">This Month</MenuItem>
                  <MenuItem value="last-month">Last Month</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={handleAdvancedFiltersClick}
                >
                  Advanced Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{formatDate(transaction.date)}</TableCell>
                  <TableCell>{transaction.invoiceNumber}</TableCell>
                  <TableCell>{transaction.supplierName}</TableCell>
                  <TableCell>{transaction.description}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(transaction.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.status.toUpperCase()}
                      color={getStatusColor(transaction.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{transaction.paymentMethod}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewTransaction(transaction)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton size="small">
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Transaction Details - {selectedTransaction?.invoiceNumber}
          </DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(selectedTransaction.date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    label={selectedTransaction.status.toUpperCase()}
                    color={getStatusColor(selectedTransaction.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Supplier</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTransaction.supplierName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Parts
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Name</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTransaction.parts.map((part, index) => (
                          <TableRow key={index}>
                            <TableCell>{part.name}</TableCell>
                            <TableCell align="right">{part.quantity}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(part.unitPrice)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(part.quantity * part.unitPrice)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <Typography variant="subtitle2">Total Amount</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2">
                              {formatCurrency(selectedTransaction.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
            <Button variant="contained" startIcon={<DownloadIcon />}>
              Download Invoice
            </Button>
          </DialogActions>
        </Dialog>

        {/* Advanced Filters Dialog */}
        <Dialog
          open={openAdvancedFiltersDialog}
          onClose={() => setOpenAdvancedFiltersDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={advancedFilters.dateRange.start}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      dateRange: { ...advancedFilters.dateRange, start: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={advancedFilters.dateRange.end}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      dateRange: { ...advancedFilters.dateRange, end: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Suppliers</InputLabel>
                  <Select
                    multiple
                    value={advancedFilters.suppliers}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setAdvancedFilters({
                        ...advancedFilters,
                        suppliers: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Ferrari South Africa">Ferrari South Africa</MenuItem>
                    <MenuItem value="McLaren Johannesburg">McLaren Johannesburg</MenuItem>
                    <MenuItem value="Porsche Centre SA">Porsche Centre SA</MenuItem>
                    <MenuItem value="Lamborghini Johannesburg">Lamborghini Johannesburg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Methods</InputLabel>
                  <Select
                    multiple
                    value={advancedFilters.paymentMethods}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setAdvancedFilters({
                        ...advancedFilters,
                        paymentMethods: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="Credit Card">Credit Card</MenuItem>
                    <MenuItem value="Debit Card">Debit Card</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min Amount"
                  type="number"
                  value={advancedFilters.amountRange.min}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      amountRange: {
                        ...advancedFilters.amountRange,
                        min: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Amount"
                  type="number"
                  value={advancedFilters.amountRange.max}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      amountRange: {
                        ...advancedFilters.amountRange,
                        max: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdvancedFiltersDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyAdvancedFilters}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 